import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import BlankCard from '~components/atoms/BlankCard';
import Image from '~components/atoms/Image';
import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import AffinFlowerLogo from '~assets/images/affin-flower-logo.webp';
import * as Styles from './_login.module.scss';

import PackageJson from '../../../package.json';

const Login = () => {
    const dispatch = useAppDispatch();

    const isLoading = useAppSelector(state => state.auth.loadings.login);
    const error = useAppSelector(state => state.auth.errors.login);
    const lastLoginTime = useAppSelector(state => state.auth.loginTime);
    const lastLogoutTime = useAppSelector(state => state.auth.logoutTime);

    const accountLocked = useAppSelector(state => state.auth.accountLocked);
    const accountLockedError = useAppSelector(state => state.auth.errors.accountLocked);

    const [username, setUsername] = useState(() => localStorage.getItem('rememberedUsername') || '');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('rememberedUsername'));

    const [remainingSeconds, setRemainingSeconds] = useState(0);

    useEffect(() => {
        if (!accountLocked) {
            setRemainingSeconds(0);
            return;
        }

        setRemainingSeconds(15 * 60);

        const interval = setInterval(() => {
            setRemainingSeconds(prev => {
                if (prev <= 1) {
                    clearInterval(interval);

                    dispatch(combinedActions.authResetAccountLocked());

                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [accountLocked, dispatch]);

    const handleLoginClick = () => {
        if (rememberMe) {
            localStorage.setItem('rememberedUsername', username);
        } else {
            localStorage.removeItem('rememberedUsername');
        }

        dispatch(combinedActions.authLoginAttempt({ username, password }));
    };

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    const countdown = `${minutes}:${seconds
        .toString()
        .padStart(2, '0')}`;

    return (
        <div className={Styles.container}>
            <BlankCard containerStyle={Styles['blank-card']}>
                <>
                    <div className={Styles.background} />

                    <div className={Styles['content-container']}>
                        <div className={Styles['content-subcontainer']}>
                            <Image src={AffinFlowerLogo} width='auto' />

                            <Text
                                label='Welcome'
                                isBold
                            />

                            <LabelledInput
                                label='User ID'
                                className={Styles.input}
                                value={username}
                                onChange={(e) => {
                                    if (error) dispatch(combinedActions.authResetLoginState());
                                    setUsername(e.target.value);
                                }}
                            />

                            <LabelledInput
                                label='Password'
                                isPassword
                                className={Styles.input}
                                onChange={(e) => {
                                    if (error) dispatch(combinedActions.authResetLoginState());
                                    setPassword(e.target.value);
                                }}
                            />

                            <div className={Styles['remember-me']}>
                                <input
                                    type='checkbox'
                                    id='remember-me'
                                    onChange={e => setRememberMe(e.target.checked)}
                                    checked={rememberMe}
                                />
                                <label>Remember me</label>
                            </div>
                        </div>

                        {lastLoginTime && lastLogoutTime
                            && (
                                <div className={Styles['user-activity-container']}>
                                    <Text label='Sign Out Successful' />

                                    <div className={Styles['user-activity-subcontainer']}>
                                        <div>
                                            <Text label='Last signed in:' />

                                            <Text label={dayjs(lastLoginTime).format('dddd, DD MMM YYYY HH:mm')} />
                                        </div>

                                        <div>
                                            <Text label='Last signed out:' />

                                            <Text label={dayjs(lastLogoutTime).format('dddd, DD MMM YYYY HH:mm')} />
                                        </div>
                                    </div>
                                </div>
                            )
                        }

                        <div className={Styles['button-container']}>
                            <ActionButton
                                label='Sign In'
                                className={Styles.button}
                                onClick={handleLoginClick}
                                isLoading={isLoading}
                                disabled={accountLocked}
                            />

                            <Text
                                isError
                                label={accountLocked
                                    ? `${accountLockedError} Please try again in (${countdown}) minutes`
                                    : error
                                }
                            />
                        </div>
                    </div>

                    <div className={Styles['version']}>
                        <p>{PackageJson.version} ({process.env.REACT_APP_ENV})</p>
                    </div>
                </>
            </BlankCard>
        </div>
    );
};

export default Login;
